// src/sharedcomponents/navbar/Navbar.js
import React, { useState, useEffect, useCallback } from "react";
import { Navbar, Nav, NavDropdown } from "react-bootstrap";
import { useHistory, Link, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import Logo from '../../assets/Logo.png';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faUser,
  faUserCircle,
  faUserPlus,
  faDownload,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';

import './Navbar.css';

library.add(faUser, faUserCircle, faUserPlus, faDownload, faTimes);

const NAV_ITEMS = [
  { to: '/', label: 'Home', end: true, ariaLabel: 'Home' },
  { to: '/ai-models', label: 'Models', ariaLabel: 'Supported AI models and providers' },
  { to: '/sales', label: 'Pricing', ariaLabel: 'Pricing' },
  { to: '/blog', label: 'Blog', ariaLabel: 'Blog' },
  { to: '/faq', label: 'FAQ', ariaLabel: 'FAQ' },
  { to: '/contactus', label: 'Contact Us', ariaLabel: 'Contact Us' },
];

const isNavItemActive = (pathname, item) => {
  if (item.end) {
    return pathname === item.to;
  }
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
};

const getDownloadLabel = () => {
  if (typeof navigator === 'undefined') return 'Download TensorPilot';
  const ua = navigator.userAgent || '';
  if (/Mac|iPhone|iPad|iPod/.test(ua)) return 'Download for macOS';
  if (/Win/.test(ua)) return 'Download for Windows';
  if (/Linux/.test(ua)) return 'Download for Linux';
  return 'Download TensorPilot';
};

const NavBarMain = () => {
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { currentUser, logout } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const userTimeoutRef = React.useRef(null);

  const closeMobileMenu = useCallback(() => setMobileMenuOpen(false), []);
  const toggleMobileMenu = useCallback(() => setMobileMenuOpen((open) => !open), []);

  const handleUserMouseEnter = () => {
    if (userTimeoutRef.current) {
      clearTimeout(userTimeoutRef.current);
      userTimeoutRef.current = null;
    }
    setShowUserDropdown(true);
  };

  const handleUserMouseLeave = () => {
    userTimeoutRef.current = setTimeout(() => {
      setShowUserDropdown(false);
    }, 150);
  };

  useEffect(() => {
    return () => {
      if (userTimeoutRef.current) {
        clearTimeout(userTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    closeMobileMenu();
  }, [location.pathname, closeMobileMenu]);

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') closeMobileMenu();
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [mobileMenuOpen, closeMobileMenu]);

  async function handleLogout() {
    try {
      await logout();
      closeMobileMenu();
      history.push("/login");
    } catch (error) {
      console.error("Failed to log out", error);
    }
  }

  const renderDesktopNavLink = (item) => {
    const active = isNavItemActive(location.pathname, item);
    return (
      <Nav.Link
        key={item.to}
        as={Link}
        to={item.to}
        aria-label={item.ariaLabel}
        aria-current={active ? 'page' : undefined}
        className={`nav-link${active ? ' nav-link--active' : ''}`}
      >
        {item.label}
      </Nav.Link>
    );
  };

  const renderMobileNavItem = (item, index) => {
    const active = isNavItemActive(location.pathname, item);

    if (active) {
      return (
        <span
          key={item.to}
          className="mobile-nav-item mobile-nav-item--active"
          aria-current="page"
          style={{ animationDelay: `${index * 40}ms` }}
        >
          <span className="mobile-nav-item-indicator" aria-hidden="true" />
          {item.label}
        </span>
      );
    }

    return (
      <Link
        key={item.to}
        to={item.to}
        className="mobile-nav-item"
        aria-label={item.ariaLabel}
        onClick={closeMobileMenu}
        style={{ animationDelay: `${index * 40}ms` }}
      >
        {item.label}
      </Link>
    );
  };

  const downloadLabel = getDownloadLabel();

  return (
    <>
      <Navbar expand="lg" sticky="top" className={mobileMenuOpen ? 'navbar--menu-open' : ''}>
        <div className="navbar-content-wrapper">
          <Navbar.Brand as={Link} to="/" className="navbar-brand" onClick={closeMobileMenu}>
            <img
              className="navbar-logo"
              src={Logo}
              alt="TensorPilot Logo"
            />
            <p className="navbar-brand-text">TensorPilot</p>
          </Navbar.Brand>

          <div className="navbar-desktop-nav d-none d-lg-flex">
            <Nav className="core-nav-items">
              {NAV_ITEMS.map(renderDesktopNavLink)}
            </Nav>

            <div className="navbar-actions-group">
              <Nav.Link
                as={Link}
                to="/download"
                aria-label="Free Download"
                className="nav-link nav-link-download"
              >
                Download
              </Nav.Link>

              {currentUser && currentUser.email ? (
                <NavDropdown
                  title={<FontAwesomeIcon icon={faUser} className="user-icon" />}
                  id="user-dropdown-desktop"
                  show={showUserDropdown}
                  onMouseEnter={handleUserMouseEnter}
                  onMouseLeave={handleUserMouseLeave}
                  align="end"
                  className="nav-item"
                >
                  <NavDropdown.Item
                    as={Link}
                    to="/profile"
                    className="navDropdown-item"
                    aria-label="View Profile"
                  >
                    Profile
                  </NavDropdown.Item>
                  <NavDropdown.Divider />
                  <NavDropdown.Item
                    as="div"
                    onClick={handleLogout}
                    className="navDropdown-item"
                    aria-label="Sign out of your account"
                  >
                    Sign Out
                  </NavDropdown.Item>
                </NavDropdown>
              ) : (
                <Nav.Link
                  as={Link}
                  to="/login"
                  className="nav-link nav-link-profile"
                  aria-label="Sign in to your account"
                >
                  <FontAwesomeIcon icon={faUserCircle} className="profile-icon" />
                </Nav.Link>
              )}
            </div>
          </div>

          <button
            type="button"
            className="navbar-mobile-toggle d-lg-none"
            aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-nav-drawer"
            onClick={toggleMobileMenu}
          >
            <div className={`toggle-icon${mobileMenuOpen ? ' toggle-icon--open' : ''}`}>
              <span />
              <span />
            </div>
          </button>
        </div>
      </Navbar>

      <div
        className={`mobile-nav-root${mobileMenuOpen ? ' mobile-nav-root--open' : ''}`}
        aria-hidden={!mobileMenuOpen}
      >
        <button
          type="button"
          className="mobile-nav-backdrop"
          aria-label="Close navigation menu"
          tabIndex={mobileMenuOpen ? 0 : -1}
          onClick={closeMobileMenu}
        />

        <div
          id="mobile-nav-drawer"
          className="mobile-nav-drawer"
          role="dialog"
          aria-modal="true"
          aria-label="Site navigation"
        >
          <div className="mobile-nav-drawer-header">
            <Link to="/" className="mobile-nav-drawer-brand" onClick={closeMobileMenu}>
              <img className="navbar-logo" src={Logo} alt="" aria-hidden="true" />
              <span className="navbar-brand-text">TensorPilot</span>
            </Link>
            <button
              type="button"
              className="mobile-nav-close"
              aria-label="Close navigation menu"
              onClick={closeMobileMenu}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          <nav className="mobile-nav-list" aria-label="Primary">
            {NAV_ITEMS.map(renderMobileNavItem)}
          </nav>

          <div className="mobile-nav-footer">
            <Link
              to="/download"
              className="mobile-nav-download"
              aria-label="Free Download"
              onClick={closeMobileMenu}
            >
              <FontAwesomeIcon icon={faDownload} className="mobile-nav-download-icon" />
              <span>{downloadLabel}</span>
            </Link>

            <div className="mobile-nav-auth">
              {currentUser && currentUser.email ? (
                <>
                  <Link to="/profile" className="mobile-nav-auth-link" onClick={closeMobileMenu}>
                    Profile
                  </Link>
                  <span className="mobile-nav-auth-divider" aria-hidden="true" />
                  <button type="button" className="mobile-nav-auth-link mobile-nav-auth-button" onClick={handleLogout}>
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="mobile-nav-auth-link" onClick={closeMobileMenu}>
                    <FontAwesomeIcon icon={faUserCircle} className="mobile-nav-auth-icon" />
                    Sign In
                  </Link>
                  <span className="mobile-nav-auth-divider" aria-hidden="true" />
                  <Link to="/signup" className="mobile-nav-auth-link" onClick={closeMobileMenu}>
                    <FontAwesomeIcon icon={faUserPlus} className="mobile-nav-auth-icon" />
                    Sign Up
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NavBarMain;
